<template>
    <div>
        <template v-if="consent">
            <GoogleMap
                :api-key="apiKey"
                :styles="style"
                class="h-full w-full"
                :class="mapClasses"
                ref="mapRef"
                :zoom="15">
                <MarkerCluster>
                    <Marker
                        v-for="(item, i) in markers"
                        :key="i"
                        :options="{
                            position: { lat: item.lat, lng: item.lng },
                            icon: {
                                url: item.icon || defaultMapMarkerIcon,
                                scaledSize: { width: 60, height: 60 }
                            }
                        }" />
                </MarkerCluster>
            </GoogleMap>
        </template>
        <slot v-else>
            <div class="h-full w-full bg-google-maps-fallback bg-cover bg-center">
                <div class="h-full w-full backdrop-blur-sm backdrop-brightness-50">
                    <div
                        class="container lg:rize-grid flex h-full w-full items-center lg:items-center">
                        <div
                            class="w-full bg-black px-14 py-10 text-white lg:col-span-2 lg:col-start-2">
                            <h3>{{ consentTitle }}</h3>
                            <p class="mb-8 mt-4">{{ consentDescription }}</p>
                            <button
                                @click="enableGoogleMaps"
                                class="group inline-flex items-center justify-center whitespace-normal">
                                <span>{{ consentButtonLabel }}</span>
                                <div class="ml-2 h-5 w-14 shrink-0">
                                    <lz-icon
                                        name="arrow"
                                        class="h-6 w-12 stroke-white stroke-2 transition-all transition-transform group-hover:translate-x-2" />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </slot>
    </div>
</template>

<script lang="ts" setup>
import { ref, defineProps, watch, onMounted, Ref } from 'vue';
import { GoogleMap, MarkerCluster, Marker } from 'vue3-google-map';
import services from '@/components/cookies/services';
import LzIcon from '@/components/base/LzIcon.vue';
import events from '@/events';
import style from './style';

interface Coordinates {
    lat: number;
    lng: number;
    icon?: string;
}
interface LzGoogleMapsProps {
    mapClasses?: string;
    consentTitle: string;
    consentDescription: string;
    consentButtonLabel: string;
    defaultMapMarkerIcon: string;
    markers: Array<Coordinates>;
    apiKey: string;
}
const props = defineProps<LzGoogleMapsProps>();
const consent = ref(false);
const mapRef: Ref<InstanceType<typeof GoogleMap> | null> = ref(null);

watch(
    () => mapRef.value?.ready,
    ready => {
        if (ready) {
            if (props.markers.length === 1) {
                mapRef.value.map.setCenter({
                    lat: props.markers[0].lat,
                    lng: props.markers[0].lng
                });
            } else {
                const bounds = new mapRef.value.api.LatLngBounds();
                props.markers.forEach(value => {
                    bounds.extend({ lat: value.lat, lng: value.lng });
                });
                mapRef.value.map.fitBounds(bounds);
            }
        }
    }
);

onMounted(() => {
    window.addEventListener(`consent-${services.googleMaps}`, (e: Event) => {
        consent.value = !!(e as CustomEvent).detail.consent;
    });
});

function enableGoogleMaps() {
    events.$emit('enable-cookies', services.googleMaps);
}
</script>
